/**
 * @brief 前端 MQTT 客户端（浏览器通过 MQTT-over-WebSocket 直连 broker）
 */

import mqtt, { MqttClient as MqttJsClient } from 'mqtt';
import logger from '@/utils/logger';
import {
  MQTT_BROKER_URL,
  MQTT_CLIENT_ID_PREFIX,
  MQTT_PASSWORD,
  MQTT_TOPIC_CMD,
  MQTT_USERNAME,
} from '@/config';
import { parseCmd, CmdEnvelope } from './commands';

export type CmdListener = (cmd: CmdEnvelope) => void;

export type MqttState = 'idle' | 'connecting' | 'connected' | 'closed';

export interface ConnectOptions {
  brokerUrl?: string;
}

const makeClientId = () =>
  `${MQTT_CLIENT_ID_PREFIX}-main-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const SECRET_KEYS = new Set(['apiKey', 'password', 'token']);

const sanitizeLogValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sanitizeLogValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, val]) => [
      key,
      SECRET_KEYS.has(key) && val ? '***' : sanitizeLogValue(val),
    ])
  );
};

const parseLogPayload = (payload: string) => {
  try {
    return sanitizeLogValue(JSON.parse(payload));
  } catch {
    return payload;
  }
};

const logMqttMessage = (direction: 'send' | 'recv', topic: string, payload: string) => {
  logger.log(`[MQTT][${direction}]`, topic, parseLogPayload(payload));
};

class MqttService {
  private client: MqttJsClient | null = null;

  private subscribedTopics = new Set<string>();

  private cmdListeners = new Set<CmdListener>();

  private state: MqttState = 'idle';

  getConnectState() {
    return this.state;
  }

  isConnected() {
    return this.state === 'connected' && !!this.client?.connected;
  }

  connect(opts: ConnectOptions = {}): Promise<void> {
    const url = opts.brokerUrl || MQTT_BROKER_URL;
    if (!url) {
      logger.debug('[MQTT] broker URL 未配置');
      return Promise.resolve();
    }
    if (this.client && (this.state === 'connected' || this.state === 'connecting')) {
      return Promise.resolve();
    }

    this.state = 'connecting';
    return new Promise((resolve, reject) => {
      try {
        let settled = false;
        const client = mqtt.connect(url, {
          clientId: makeClientId(),
          username: MQTT_USERNAME || undefined,
          password: MQTT_PASSWORD || undefined,
          clean: true,
          reconnectPeriod: 3000,
          connectTimeout: 10000,
        });
        this.client = client;

        client.on('connect', () => {
          this.state = 'connected';
          logger.debug('[MQTT] broker connected', url);
          this.subscribedTopics.forEach((t) => client.subscribe(t));
          if (!settled) {
            settled = true;
            resolve();
          }
        });
        client.on('reconnect', () => {
          this.state = 'connecting';
        });
        client.on('close', () => {
          this.state = 'closed';
          logger.debug('[MQTT] broker closed');
        });
        client.on('error', (err) => {
          logger.debug('[MQTT] broker error', err);
          if (!settled && this.state === 'connecting') {
            settled = true;
            reject(err);
          }
        });
        client.on('message', (topic, payload) =>
          this.handleMessage(topic, payload.toString('utf-8'))
        );
      } catch (e) {
        this.state = 'closed';
        reject(e);
      }
    });
  }

  disconnect() {
    if (this.client) {
      try {
        this.client.end(true);
      } catch {
        /* noop */
      }
      this.client = null;
    }
    this.subscribedTopics.clear();
    this.cmdListeners.clear();
    this.state = 'idle';
  }

  subscribeCmd(roomId: string) {
    const topic = MQTT_TOPIC_CMD(roomId);
    this.subscribedTopics.add(topic);
    if (this.client?.connected) this.client.subscribe(topic);
  }

  onCmd(listener: CmdListener) {
    this.cmdListeners.add(listener);
    return () => {
      this.cmdListeners.delete(listener);
    };
  }

  publish(topic: string, payload: string): void {
    if (!this.client?.connected) {
      logger.debug('[MQTT] 未连接，publish 丢弃', topic);
      return;
    }
    logMqttMessage('send', topic, payload);
    this.client.publish(topic, payload);
  }

  private handleMessage(topic: string, payload: string) {
    logMqttMessage('recv', topic, payload);
    let payloadObj: unknown;
    try {
      payloadObj = JSON.parse(payload);
    } catch {
      logger.debug('[MQTT] payload not json', topic);
      return;
    }
    const cmd = parseCmd(payloadObj);
    if (!cmd) {
      logger.debug('[MQTT] invalid cmd', payloadObj);
      return;
    }
    this.cmdListeners.forEach((l) => {
      try {
        l(cmd);
      } catch (e) {
        logger.debug('[MQTT] listener error', e);
      }
    });
  }
}

export default new MqttService();
